// Onboarding Client - OFD API
/* eslint-disable no-unused-vars */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios';
import {
  OnboardingPageData,
  UserFranchiseList,
  RetailerListModel,
  RetailerIdentifierModel,
  OnboardingCataloguesModel,
  RetailerOnboardingDataModel,
  RetailerEdnModel,
} from './ofdTypes';
import { isAxiosError, throwException } from './ofdUtils';

type JsonParseReviver = (key: string, value: unknown) => unknown;

export class OnboardingClient {
  protected instance: AxiosInstance;
  protected baseUrl: string;
  protected jsonParseReviver?: JsonParseReviver;

  constructor(baseUrl?: string, instance?: AxiosInstance) {
    this.instance = instance || axios.create();
    this.baseUrl = baseUrl ?? '';
  }

  /**
     * @return Success
     */
  pagedata(cancelToken?: CancelToken): Promise<OnboardingPageData> {
    let url_ = this.baseUrl + '/Onboarding/pagedata';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      method: 'GET',
      url: url_,
      headers: {
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processPagedata(_response);
    });
  }

  protected processPagedata(response: AxiosResponse): Promise<OnboardingPageData> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: OnboardingPageData | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<OnboardingPageData>(result200 as OnboardingPageData);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<OnboardingPageData>(null as unknown as OnboardingPageData);
  }

  /**
     * @param body (optional) 
     * @return Success
     */
  retailers(body?: UserFranchiseList | undefined, cancelToken?: CancelToken): Promise<RetailerListModel> {
    let url_ = this.baseUrl + '/Onboarding/retailers';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_: AxiosRequestConfig = {
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processRetailers(_response);
    });
  }

  protected processRetailers(response: AxiosResponse): Promise<RetailerListModel> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: RetailerListModel | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<RetailerListModel>(result200 as RetailerListModel);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<RetailerListModel>(null as unknown as RetailerListModel);
  }

  /**
     * @param companyId (optional) 
     * @return Success
     */
  getActiveRetailersByCompany(companyId?: number | undefined, cancelToken?: CancelToken): Promise<RetailerIdentifierModel[]> {
    let url_ = this.baseUrl + '/Onboarding/getActiveRetailersByCompany?';
    if (companyId === null)
      throw new Error('The parameter \'companyId\' cannot be null.');
    else if (companyId !== undefined)
      url_ += 'companyId=' + encodeURIComponent('' + companyId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      method: 'GET',
      url: url_,
      headers: {
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processGetActiveRetailersByCompany(_response);
    });
  }

  protected processGetActiveRetailersByCompany(response: AxiosResponse): Promise<RetailerIdentifierModel[]> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: RetailerIdentifierModel[] | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<RetailerIdentifierModel[]>(result200 as RetailerIdentifierModel[]);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<RetailerIdentifierModel[]>(null as unknown as RetailerIdentifierModel[]);
  }

  /**
     * @param storeId (optional) 
     * @param retailerId (optional) 
     * @return Success
     */
  storevalidate(storeId?: string | undefined, retailerId?: number | undefined, cancelToken?: CancelToken): Promise<boolean> {
    let url_ = this.baseUrl + '/Onboarding/storevalidate?';
    if (storeId === null)
      throw new Error('The parameter \'storeId\' cannot be null.');
    else if (storeId !== undefined)
      url_ += 'storeId=' + encodeURIComponent('' + storeId) + '&';
    if (retailerId === null)
      throw new Error('The parameter \'retailerId\' cannot be null.');
    else if (retailerId !== undefined)
      url_ += 'retailerId=' + encodeURIComponent('' + retailerId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      method: 'POST',
      url: url_,
      headers: {
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processStorevalidate(_response);
    });
  }

  protected processStorevalidate(response: AxiosResponse): Promise<boolean> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: boolean | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<boolean>(result200 as boolean);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<boolean>(false);
  }

  /**
     * @param franchiseName (optional) 
     * @return Success
     */
  catalogues(franchiseName?: string | undefined, cancelToken?: CancelToken): Promise<OnboardingCataloguesModel> {
    let url_ = this.baseUrl + '/Onboarding/catalogues?';
    if (franchiseName === null)
      throw new Error('The parameter \'franchiseName\' cannot be null.');
    else if (franchiseName !== undefined)
      url_ += 'franchiseName=' + encodeURIComponent('' + franchiseName) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      method: 'GET',
      url: url_,
      headers: {
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processCatalogues(_response);
    });
  }

  protected processCatalogues(response: AxiosResponse): Promise<OnboardingCataloguesModel> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: OnboardingCataloguesModel | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<OnboardingCataloguesModel>(result200 as OnboardingCataloguesModel);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<OnboardingCataloguesModel>(null as unknown as OnboardingCataloguesModel);
  }

  /**
     * @param retailerId (optional) 
     * @param body (optional) 
     * @return Success
     */
  retailer(retailerId?: number | undefined, body?: UserFranchiseList | undefined, cancelToken?: CancelToken): Promise<RetailerOnboardingDataModel> {
    let url_ = this.baseUrl + '/Onboarding/retailer?';
    if (retailerId === null)
      throw new Error('The parameter \'retailerId\' cannot be null.');
    else if (retailerId !== undefined)
      url_ += 'retailerId=' + encodeURIComponent('' + retailerId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_: AxiosRequestConfig = {
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processRetailer(_response);
    });
  }

  protected processRetailer(response: AxiosResponse): Promise<RetailerOnboardingDataModel> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: RetailerOnboardingDataModel | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<RetailerOnboardingDataModel>(result200 as RetailerOnboardingDataModel);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<RetailerOnboardingDataModel>(null as unknown as RetailerOnboardingDataModel);
  }

  /**
     * @param body (optional) 
     * @return Success
     */
  update(body?: RetailerOnboardingDataModel | undefined, cancelToken?: CancelToken): Promise<number> {
    let url_ = this.baseUrl + '/Onboarding/update';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_: AxiosRequestConfig = {
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUpdate(_response);
    });
  }

  protected processUpdate(response: AxiosResponse): Promise<number> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: number | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<number>(result200 as number);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<number>(0);
  }

  /**
     * @param retailerId (optional) 
     * @return Success
     */
  delete(retailerId?: number | undefined, cancelToken?: CancelToken): Promise<void> {
    let url_ = this.baseUrl + '/Onboarding/delete?';
    if (retailerId === null)
      throw new Error('The parameter \'retailerId\' cannot be null.');
    else if (retailerId !== undefined)
      url_ += 'retailerId=' + encodeURIComponent('' + retailerId) + '&';
    url_ = url_.replace(/[?&]$/, '');

    const options_: AxiosRequestConfig = {
      method: 'DELETE',
      url: url_,
      headers: {
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processDelete(_response);
    });
  }

  protected processDelete(response: AxiosResponse): Promise<void> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      return Promise.resolve<void>(undefined);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<void>(undefined);
  }

  /**
     * @param body (optional) 
     * @return Success
     */
  updateShippedRetailerOrders(body?: RetailerEdnModel | undefined, cancelToken?: CancelToken): Promise<string> {
    let url_ = this.baseUrl + '/Onboarding/updateShippedRetailerOrders';
    url_ = url_.replace(/[?&]$/, '');

    const content_ = JSON.stringify(body);

    const options_: AxiosRequestConfig = {
      data: content_,
      method: 'POST',
      url: url_,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/plain',
      },
      cancelToken,
    };

    return this.instance.request(options_).catch((_error: unknown) => {
      if (isAxiosError(_error) && _error.response) {
        return _error.response;
      } else {
        throw _error;
      }
    }).then((_response: AxiosResponse) => {
      return this.processUpdateShippedRetailerOrders(_response);
    });
  }

  protected processUpdateShippedRetailerOrders(response: AxiosResponse): Promise<string> {
    const status = response.status;
    const _headers: Record<string, string> = {};
    if (response.headers && typeof response.headers === 'object') {
      for (const k in response.headers) {
        if (Object.prototype.hasOwnProperty.call(response.headers, k)) {
          _headers[k] = String(response.headers[k]);
        }
      }
    }
    if (status === 200) {
      const _responseText = response.data;
      let result200: string | null = null;
      const resultData200 = _responseText;
      result200 = JSON.parse(resultData200);
      return Promise.resolve<string>(result200 as string);

    } else if (status !== 200 && status !== 204) {
      const _responseText = response.data;
      return throwException('An unexpected server error occurred.', status, _responseText, _headers);
    }
    return Promise.resolve<string>('');
  }
}
